import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram, Youtube, Music2, Facebook, MessageCircle, Mail } from "lucide-react";
import api from "@/lib/api";
import { trackClick } from "@/lib/track";
import { openWhatsAppHub } from "@/components/WhatsAppHub";
import { NewsletterForm } from "@/components/NewsletterForm";

const COLS = [
  {
    title: "Explore",
    links: [
      { label: "Trips", href: "/trips" },
      { label: "Roamies IRL", href: "/irl" },
      { label: "How It Works", href: "/#how-it-works" },
      { label: "About", href: "/#about" },
      { label: "FAQ", href: "/faq" },
    ],
  },
  {
    title: "Support",
    links: [
      { label: "Contact", href: "/#contact" },
      { label: "Safety", href: "/legal/safety" },
      { label: "Cancellation Policy", href: "/legal/cancellation" },
    ],
  },
  {
    title: "Legal",
    links: [
      { label: "Privacy Policy", href: "/legal/privacy" },
      { label: "Terms & Conditions", href: "/legal/terms" },
      { label: "Travel Waiver", href: "/legal/waiver" },
    ],
  },
];

export const Footer = () => {
  const [social, setSocial] = useState({});

  useEffect(() => {
    api.get("/settings/public").then((r) => setSocial(r.data)).catch(() => {});
  }, []);

  const socialLinks = [
    { key: "instagram", label: "Instagram", icon: Instagram },
    { key: "facebook", label: "Facebook", icon: Facebook },
    { key: "tiktok", label: "TikTok", icon: Music2 },
    { key: "youtube", label: "YouTube", icon: Youtube },
  ].filter((s) => social[s.key]);

  return (
    <footer data-testid="site-footer" className="bg-ink text-cream noise-bg">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-20">
        <div className="grid grid-cols-2 md:grid-cols-12 gap-10">
          <div className="col-span-2 md:col-span-4">
            <p className="font-display text-4xl sm:text-5xl leading-[0.95] uppercase">
              Roamies<span className="text-flame">.</span>
            </p>
            <p className="mt-3 text-sm text-cream/60 font-semibold">Show up solo. Leave with a family.</p>
            {(socialLinks.length > 0 || social.email) && (
              <div className="flex gap-3 mt-6 flex-wrap">
                {socialLinks.map((s) => (
                  <a key={s.key} href={social[s.key]} target="_blank" rel="noopener noreferrer"
                    onClick={() => trackClick(s.key)} aria-label={s.label}
                    data-testid={`footer-${s.key}-link`}
                    className="p-3 border border-cream/20 hover:bg-flame hover:border-flame transition-colors">
                    <s.icon className="w-4 h-4" />
                  </a>
                ))}
                <button onClick={() => openWhatsAppHub()} aria-label="WhatsApp"
                  data-testid="footer-whatsapp-link"
                  className="p-3 border border-cream/20 hover:bg-flame hover:border-flame transition-colors">
                  <MessageCircle className="w-4 h-4" />
                </button>
                {social.email && (
                  <a href={`mailto:${social.email}`} onClick={() => trackClick("email")} aria-label="Email"
                    data-testid="footer-email-link"
                    className="p-3 border border-cream/20 hover:bg-flame hover:border-flame transition-colors">
                    <Mail className="w-4 h-4" />
                  </a>
                )}
              </div>
            )}
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="md:col-span-2">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-cream/50 mb-5">{col.title}</p>
              <ul className="space-y-3 text-sm font-semibold">
                {col.links.map((l) => (
                  <li key={l.label}>
                    {l.href.startsWith("/#") ? (
                      <a href={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-flame transition-colors">{l.label}</a>
                    ) : (
                      <Link to={l.href} data-testid={`footer-link-${l.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-flame transition-colors">{l.label}</Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="col-span-2 md:col-span-2">
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-cream/50 mb-5">Drops, First</p>
            <p className="text-sm text-cream/70 mb-4">Get the next drop before everyone else.</p>
            <NewsletterForm />
          </div>
        </div>

        <div className="border-t border-cream/10 mt-16 pt-8 flex flex-col sm:flex-row justify-between gap-4 text-xs text-cream/40">
          <p data-testid="footer-copyright">© {new Date().getFullYear()} Roamies. Small crews, big trips.</p>
          <p>Ages 18–45 · Small curated groups</p>
        </div>

        <p aria-hidden="true"
          className="mt-14 font-display uppercase leading-none text-center select-none pointer-events-none text-[19vw] text-transparent"
          style={{ WebkitTextStroke: "1px rgba(247,247,245,0.14)" }}
          data-testid="footer-watermark">
          Roamies
        </p>
      </div>
    </footer>
  );
};

